import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import './dashboard.scss';
import { useHeaderTitle } from "../../components/layout/layout";
import { PiOfficeChair, PiSuitcaseSimpleLight } from "react-icons/pi";
import { LuMapPin } from "react-icons/lu";
import { GoClock } from "react-icons/go";
import dashboardApi from "../../services/dashboardAPi";
import { OverviewCount } from "./overviewcount";
import { AuthContext } from "../../context/AuthContext";

import Chart from 'react-apexcharts';
import jobApi from "../../services/jobApi";
import { ThreeDotLoader } from "../../components/Loader/Loader"; // Add this import

import ReactCountryFlag from "react-country-flag";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";

countries.registerLocale(enLocale);

export default function Dashboard() {
  const dynamicColors = [
    "#02DDAC", "#23D4EB", "#01ABE8", "#FFA500", "#FF6347", "#8A2BE2", "#00CED1"
  ];


  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false); // Loader state
  const { rolePermissions, countryFilterValue, yearFilterValue, accountFilterValue, departmentFilterValue } = useContext(AuthContext);
  const currentYear = new Date().getFullYear();
  const [openings, setOpenings] = useState([]);
  const [todayEvents, setTodayEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [jobsOverviewCount, setJobsOverviewCount] = useState(null)
  const [jobActivityLog, setJobActivityLog] = useState([]);


  const { setHeaderTitle } = useHeaderTitle();
  useEffect(() => {
    setHeaderTitle("Dashboard");
  }, []);


  const [chartData, setChartData] = useState({
    series: [],
    options: {
      chart: { type: "line", height: 300, toolbar: { show: false } },
      stroke: { width: 2, curve: "smooth" },
      colors: ["#01ABE8", "#02DDAC"],
      xaxis: { categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] },
      markers: { size: 3, colors: ["#01ABE8", "#02DDAC"], hover: { sizeOffset: 3 } },
      tooltip: { shared: true, intersect: false },
      legend: { position: "bottom" },
      grid: { show: false },
    },
  });

  const [vacancyChartData, setVacancyChartData] = useState({
    series: [],
    options: {
      chart: { type: "bar", stacked: true, toolbar: { show: false } },
      colors: ["#02DDAC", "#01ABE8"],
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "30px",
        },
      },
      xaxis: { categories: [] },
      legend: { position: "bottom", labels: { colors: "#333" } },
      grid: { show: false },
      dataLabels: { enabled: false },
    },
  });

  const [analyticChartData, setAnalyticChartData] = useState({
    series: [],
    options: {
      chart: {
        type: "donut",
      },
      labels: [],
      colors: [],
      legend: {
        position: "right",
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        y: {
          formatter: (val) => `${val} Positions`,
        },
      },
    },
  });



  const buildDashboardQuery = () => {
    let query = "";
    const year = (yearFilterValue && yearFilterValue > 0) ? yearFilterValue : new Date().getFullYear();
    query += `&year=${year}`;
    if (countryFilterValue && countryFilterValue > 0) query += `&country=${countryFilterValue}`;
    if (accountFilterValue && accountFilterValue > 0) query += `&account=${accountFilterValue}`;
    if (departmentFilterValue && departmentFilterValue > 0) query += `&department=${departmentFilterValue}`;
    return query ? `?${query.slice(1)}` : "";
  };


  useEffect(() => {
    const fetchAllDashboardData = async () => {
      setIsLoading(true);
      try {
        const queryString = buildDashboardQuery();

        // Fetch all dashboard data in parallel or sequence as needed
        await Promise.all([
          (async () => {
            const response = await dashboardApi.getVacancyOverview(queryString);
            if (response?.data) {
              setChartData((prev) => ({
                ...prev,
                series: response.data.series || [],
                options: {
                  ...prev.options,
                  xaxis: {
                    ...prev.options.xaxis,
                    categories: response.data.categories || prev.options.xaxis.categories,
                  },
                },
              }));
            }
          })(),
          (async () => {
            const response = await dashboardApi.getCurrentOpenings(queryString);
            if (Array.isArray(response?.data)) setOpenings(response.data);
          })(),
          (async () => {
            // debugger
            const response = await dashboardApi.getVacanciesByClients(queryString);
            const data = response?.data;
            if (Array.isArray(data)) {
              const categories = data.map((item) => item.clientName);
              const filledData = data.map((item) => item.filled);
              const positionsData = data.map((item) => item.positions);
              setVacancyChartData((prev) => ({
                ...prev,
                series: [
                  { name: "Filled", data: filledData },
                  { name: "Positions", data: positionsData },
                ],
                options: {
                  ...prev.options,
                  xaxis: {
                    ...prev.options.xaxis,
                    categories: categories,
                  },
                },
              }));
            }
          })(),
          (async () => {
            const response = await dashboardApi.getPositionsByCountry(queryString)
            const data = response?.data;
            if (Array.isArray(data)) {
              const labels = data.map(item => item.countryName);
              const series = data.map(item => Number(item.positionsCreated));
              const colors = (dynamicColors || []).slice(0, data.length);
              setAnalyticChartData((prev) => ({
                ...prev,
                series,
                options: {
                  ...prev.options,
                  labels,
                  colors,
                },
              }));
            }
          })(),
          (async () => {
            const response = await dashboardApi.getUpcomingCandidateEvents(queryString);
            if (Array.isArray(response?.data)) {
              const now = new Date();
              const today = now.toISOString().split("T")[0];
              const todayEvents = [];
              const upcomingEvents = [];
              for (const event of response.data) {
                const eventDate = new Date(event.activityDate);
                const eventDay = eventDate.toISOString().split("T")[0];
                if (eventDay === today) {
                  todayEvents.push(event);
                } else if (eventDate > now) {
                  upcomingEvents.push(event);
                }
              }
              todayEvents.sort((a, b) => new Date(a.activityDate) - new Date(b.activityDate));
              upcomingEvents.sort((a, b) => new Date(a.activityDate) - new Date(b.activityDate));
              setTodayEvents(todayEvents);
              setUpcomingEvents(upcomingEvents);
            }
          })(),
          (async () => {
            const response = await dashboardApi.getJobActivityLog(queryString);
            if (Array.isArray(response?.data)) setJobActivityLog(response.data);
          })(),
        ]);
      } catch (error) {
        // Handle errors if needed
      } finally {
        setIsLoading(false);
      }
    };
    fetchAllDashboardData();
    // eslint-disable-next-line
  }, [countryFilterValue, yearFilterValue, accountFilterValue, departmentFilterValue]);

  const countryCodes = ["IN", "US", "DE", "FR"];

  return (
    <section className="dashboard-wrapper">
      {isLoading && <ThreeDotLoader />} {/* Loader overlay */}
      <section className="d-flex w-100">
        <OverviewCount />
      </section>
      {/* <h5 className="mb-0">
                Overview
            </h5>
            <p className="gray-bold">
                Recruitment overview of this month
            </p>
            {/* <div className="row overview-card-container">
                <div className="col-6 card-holder mb-4">
                    <div className="card p-2 card-01">
                        <div className="d-flex align-items-center">
                            <div className="left">
                                <h2 className="fw-bold primary-blue">
                                    {jobsOverviewCount?.openPositionsCount || 0}
                                </h2>
                                <p className="mb-0">
                                    Openings
                                </p>
                            </div>
                            <div className="icon ms-auto primary-blue">
                                <svg xmlns="http://www.w3.org/2000/svg" width="29.5" height="29.699" viewBox="0 0 29.5 29.699">
                                    <g id="chair-2-svgrepo-com" transform="translate(0.75 0.75)">
                                        <path id="Path_583" data-name="Path 583" d="M6,15.9a1.4,1.4,0,0,1,1.4-1.4h14a1.4,1.4,0,0,1,1.4,1.4A2.8,2.8,0,0,1,20,18.7H8.8A2.8,2.8,0,0,1,6,15.9Z" transform="translate(-0.4 3)" fill="none" stroke="#0583d0" stroke-width="1.5" />
                                        <path id="Path_584" data-name="Path 584" d="M6.748,8.672c.338-2.816.507-4.224,1.321-5.186a4.2,4.2,0,0,1,.893-.792C10.014,2,11.432,2,14.268,2s4.254,0,5.306.694a4.2,4.2,0,0,1,.893.792c.814.962.983,2.37,1.321,5.186l.127,1.061c.351,2.922.526,4.384-.31,5.325S19.3,16,16.356,16H12.181c-2.943,0-4.415,0-5.251-.942s-.66-2.4-.309-5.325Z" transform="translate(-0.268 -2)" fill="none" stroke="#0583d0" stroke-width="1.5" />
                                        <path id="Path_585" data-name="Path 585" d="M12,12v2.8" transform="translate(2 2)" fill="none" stroke="#0583d0" stroke-width="1.5" />
                                        <path id="Path_586" data-name="Path 586" d="M16,26.2V23.4m0,0V19.9m0,3.5.653.163A4.033,4.033,0,0,1,19.5,26.2M16,23.4l-.653.163A4.033,4.033,0,0,0,12.5,26.2M7.6,17.8,6.389,14.166a5.781,5.781,0,0,0-.253-.675,3.3,3.3,0,0,0-.222-.416C5.275,12.025,4.425,11.5,2,11.5m22.4,6.3,1.211-3.634a5.763,5.763,0,0,1,.253-.675,3.3,3.3,0,0,1,.222-.416C26.725,12.025,27.574,11.5,30,11.5" transform="translate(-2 1.8)" fill="none" stroke="#0583d0" stroke-linecap="round" stroke-width="1.5" />
                                    </g>
                                </svg>

                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-6 card-holder mb-4">
                    <div className="card p-2 card-02">
                        <div className="d-flex align-items-center">
                            <div className="left">
                                <h2 className="fw-bold primary-color">
                                {jobsOverviewCount?.profileCount || 0}
                                </h2>
                                <p className="mb-0">
                                    Profiles
                                </p>
                            </div>
                            <div className="icon ms-auto primary-color">
                                <svg xmlns="http://www.w3.org/2000/svg" width="23.05" height="25" viewBox="0 0 23.05 25">
                                    <g id="user-profile-svgrepo-com" transform="translate(-19.968)">
                                        <g id="Group_1834" data-name="Group 1834" transform="translate(27.454)">
                                            <g id="Group_1833" data-name="Group 1833">
                                                <path id="Path_589" data-name="Path 589" d="M177.319,0a4.039,4.039,0,1,0,4.039,4.04A4.043,4.043,0,0,0,177.319,0Zm0,7.3a3.258,3.258,0,1,1,3.258-3.259A3.262,3.262,0,0,1,177.319,7.3Z" transform="translate(-173.28)" fill="#23d4eb" />
                                            </g>
                                        </g>
                                        <g id="Group_1836" data-name="Group 1836" transform="translate(22.529 9.078)">
                                            <g id="Group_1835" data-name="Group 1835">
                                                <path id="Path_590" data-name="Path 590" d="M85.321,186.014l-.31-.094-3.631,2.737-3.63-2.737-.31.094a7.135,7.135,0,0,0-5.023,6.689l.008.633H90.337l.008-.633A7.135,7.135,0,0,0,85.321,186.014ZM73.2,192.554a6.269,6.269,0,0,1,4.395-5.772l3.313,2.5.47.355.47-.355,3.313-2.5a6.271,6.271,0,0,1,4.4,5.772Z" transform="translate(-72.416 -185.92)" fill="#23d4eb" />
                                            </g>
                                        </g>
                                        <g id="Group_1838" data-name="Group 1838" transform="translate(19.968 20.375)">
                                            <g id="Group_1837" data-name="Group 1837" transform="translate(0)">
                                                <rect id="Rectangle_573" data-name="Rectangle 573" width="23.05" height="0.781" fill="#23d4eb" />
                                            </g>
                                        </g>
                                        <g id="Group_1840" data-name="Group 1840" transform="translate(19.968 24.219)">
                                            <g id="Group_1839" data-name="Group 1839" transform="translate(0)">
                                                <rect id="Rectangle_574" data-name="Rectangle 574" width="23.05" height="0.781" fill="#23d4eb" />
                                            </g>
                                        </g>
                                    </g>
                                </svg>


                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-6 card-holder mb-4">
                    <div className="card p-2 card-03">
                        <div className="d-flex align-items-center">
                            <div className="left">
                                <h2 className="fw-bold primary-dark">
                                {jobsOverviewCount?.shortlistedCount || 0}
                                </h2>
                                <p className="mb-0">
                                    Shortlisted
                                </p>
                            </div>
                            <div className="icon ms-auto primary-dark">
                                <svg xmlns="http://www.w3.org/2000/svg" width="21.727" height="25" viewBox="0 0 21.727 25">
                                    <g id="selected-document-file-svgrepo-com" transform="translate(-33.515)">
                                        <path id="Path_591" data-name="Path 591" d="M116.432,372.443h8.385a5.371,5.371,0,0,1,.462-1.005h-8.846v1.005Z" transform="translate(-78.868 -353.301)" fill="#34e9ed" />
                                        <rect id="Rectangle_575" data-name="Rectangle 575" width="10.508" height="1.004" transform="translate(37.563 14.064)" fill="#34e9ed" />
                                        <rect id="Rectangle_576" data-name="Rectangle 576" width="10.508" height="1.004" transform="translate(37.563 9.993)" fill="#34e9ed" />
                                        <rect id="Rectangle_577" data-name="Rectangle 577" width="4.953" height="1.004" transform="translate(43.118 5.922)" fill="#34e9ed" />
                                        <path id="Path_592" data-name="Path 592" d="M35.657,22.352a.937.937,0,0,1-.938-.937V8.1h5.732A1.15,1.15,0,0,0,41.6,6.947V1.2h8.381a.939.939,0,0,1,.937.937V15.42c.082-.007.16-.007.242-.007a5.353,5.353,0,0,1,.962.085V2.142A2.144,2.144,0,0,0,49.979,0h-8.69a2.343,2.343,0,0,0-1.666.689L34.2,6.108a2.343,2.343,0,0,0-.689,1.666V21.415a2.141,2.141,0,0,0,2.142,2.141H46.328a5.462,5.462,0,0,1-.483-1.2H35.657ZM40.529,1.488V6.3c0,.519-.2.721-.724.721H34.989Z" fill="#34e9ed" />
                                        <path id="Path_593" data-name="Path 593" d="M315.287,344.719a4.084,4.084,0,1,0,4.084,4.084A4.084,4.084,0,0,0,315.287,344.719Zm2.639,2.752-2.18,2.644a.8.8,0,0,1-1.169.1l-1.325-1.191a.5.5,0,0,1-.037-.712l.178-.2a.5.5,0,0,1,.714-.032l1.107,1.018,2.245-2.074a.324.324,0,0,1,.467.445Z" transform="translate(-264.129 -327.887)" fill="#34e9ed" />
                                    </g>
                                </svg>

                            </div>
                        </div>
                    </div>
                </div>
           

                <div className="col-6 card-holder mb-4">
                    <div className="card p-2 card-06">
                        <div className="d-flex align-items-center">
                            <div className="left">
                                <h2 className="fw-bold success-color">
                                {jobsOverviewCount?.onBoardedCount || 0}
                                </h2>
                                <p className="mb-0">
                                    Onboarded
                                </p>
                            </div>
                            <div className="icon ms-auto success-color">
                                <svg xmlns="http://www.w3.org/2000/svg" width="30" height="20.351" viewBox="0 0 30 20.351">
                                    <path id="handshake-thin-svgrepo-com" d="M36.692,59.514l-3.045-5.832a1.493,1.493,0,0,0-1.982-.641l-2.987,1.494H26.425l-4.183-1.893a1.488,1.488,0,0,0-1.028-.073L15,54.38l-2.944-1.472a1.493,1.493,0,0,0-1.982.641L7.029,59.381A1.486,1.486,0,0,0,7.682,61.4L10.7,62.906l6.729,5.262a1.475,1.475,0,0,0,.555.271l7.176,1.794a1.475,1.475,0,0,0,.357.044,1.494,1.494,0,0,0,1.054-.435l4.556-4.556.026-.028,1.916-2.241,2.971-1.485a1.486,1.486,0,0,0,.653-2.017ZM30.7,64.266l-5.078-3.693a.5.5,0,0,0-.589,0l-1.585,1.189a3.485,3.485,0,0,1-4.16,0l-.671-.5a.5.5,0,0,1-.053-.746l4.846-4.846a.5.5,0,0,1,.35-.145h4.738l3.669,7.025ZM7.875,60.221a.492.492,0,0,1,.032-.382l3.045-5.832a.5.5,0,0,1,.441-.265.489.489,0,0,1,.219.052l2.641,1.32-3.5,6.71L8.125,60.512A.491.491,0,0,1,7.875,60.221Zm17.994,8.92a.5.5,0,0,1-.471.13l-7.176-1.794a.491.491,0,0,1-.185-.09l-6.444-5.039,3.661-7.011L21.49,53.52a.5.5,0,0,1,.343.024l2.189.99h-.265a1.476,1.476,0,0,0-1.051.435L17.86,59.815a1.486,1.486,0,0,0,.159,2.239l.671.5a4.481,4.481,0,0,0,5.349,0l1.292-.969L30.016,65Zm9.977-8.788a.491.491,0,0,1-.25.291l-2.625,1.313-3.5-6.71,2.641-1.32a.5.5,0,0,1,.66.214l3.045,5.832a.492.492,0,0,1,.032.382ZM20.36,72.485a.495.495,0,0,1-.6.36l-3.732-.933a1.494,1.494,0,0,1-.615-.32L12.62,69.165a.5.5,0,0,1,.65-.748l2.793,2.427a.5.5,0,0,0,.205.107L20,71.884a.5.5,0,0,1,.36.6Z" transform="translate(-6.86 -52.509)" fill="#02ddac" />
                                </svg>


                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-12 card-holder mb-4">
                    <div className="card p-2 card-05">
                        <div className="d-flex align-items-center">
                            <div className="left">
                                <h2 className="fw-bold reddish-color">
                                {jobsOverviewCount?.rejectedCount || 0}
                                </h2>
                                <p className="mb-0">
                                    Rejected
                                </p>
                            </div>
                            <div className="icon ms-auto reddish-color">
                                <svg xmlns="http://www.w3.org/2000/svg" width="23" height="19.714" viewBox="0 0 23 19.714">
                                    <g id="text-change-reject-svgrepo-com" transform="translate(-2 -5)">
                                        <path id="Path_637" data-name="Path 637" d="M18.024,12A6.024,6.024,0,1,1,12,18.024,6.024,6.024,0,0,1,18.024,12Zm-2.712,3.312a.548.548,0,0,0,0,.774l1.938,1.938L15.315,19.96a.548.548,0,1,0,.774.774L18.024,18.8l1.938,1.937a.548.548,0,0,0,.774-.774L18.8,18.025l1.94-1.938a.548.548,0,0,0-.774-.774l-1.94,1.938-1.939-1.938A.548.548,0,0,0,15.312,15.312Z" transform="translate(0.952 0.667)" fill="#ea7676" />
                                        <path id="Path_638" data-name="Path 638" d="M11.878,17H2.821l-.111.007a.822.822,0,0,0,.111,1.635h9.119a7.178,7.178,0,0,1-.084-1.1Q11.857,17.271,11.878,17Z" transform="translate(0 1.143)" fill="#ea7676" />
                                        <path id="Path_639" data-name="Path 639" d="M12.659,14.643H2.821a.822.822,0,0,1-.111-1.635L2.821,13H13.839A7.139,7.139,0,0,0,12.659,14.643Z" transform="translate(0 0.762)" fill="#ea7676" />
                                        <path id="Path_640" data-name="Path 640" d="M23.083,9H2.821l-.111.008a.821.821,0,0,0,.111,1.635H23.083l.111-.007A.822.822,0,0,0,23.083,9Z" transform="translate(0 0.381)" fill="#ea7676" />
                                        <path id="Path_641" data-name="Path 641" d="M23.083,5H2.821l-.111.008a.821.821,0,0,0,.111,1.635H23.083l.111-.008A.821.821,0,0,0,23.083,5Z" transform="translate(0)" fill="#ea7676" />
                                    </g>
                                </svg>




                            </div>
                        </div>
                    </div>
                </div>
            </div> */}

      <div className="row">
        <div className="col-12 col-md-12 col-lg-6 mb-4">
          <div className="card h-100 p-3">
            <h6 className="mb-3">Vacancy Overview</h6>
            <div className="chart-holder">
              <Chart options={chartData?.options} series={chartData?.series} type="line" height={300} />
            </div>
          </div>
        </div>


        <div className="col-12 col-md-6 col-lg-3 mb-4">
          <div className="card h-100 p-3 current-opening" >
            <h6 className="mb-3">Current Openings</h6>
            <ul className="list-holder ps-0">
  {openings?.map((job) => {
    // Split countryCodes string into array and trim spaces
    const jobCountryCodes = job.countryCodes
      ? job.countryCodes.split(',').map(code => code.trim())
      : [];
    return (
      <li
        key={job.id}
        className="mb-3 position-relative"
        style={{ cursor: "pointer" }}
        onClick={() => navigate(`/job-list/job-details/${job.id}`, { state: { item: job } })}
      >
        <p className="mb-1 fw-semibold j-title" title={job.jobTitle}>{job.jobTitle}</p>
        <div className="d-flex gap-3 infos align-items-center">
          <div className="d-flex gap-1 infos align-items-center">
            <span><PiOfficeChair /></span>
            <p className="mb-0">{job.numberOfPositions - job.filledPositions}/{job.numberOfPositions}</p>
          </div>
          <div className="ms-auto limited-text">
            <div className="d-flex gap-1 infos align-items-center">
              <span><PiSuitcaseSimpleLight /></span>
              <p className="mb-0 limited-text">{job.accountName}</p>
            </div>
          </div>
        </div>
        <div className="d-flex gap-3 infos align-items-center">
          <div className="d-flex gap-1 infos align-items-center limited-text mx-width-50" title={job.locations}>
            <span><LuMapPin /></span>
            <p className="mb-0 limited-text">{job.locations}</p>
          </div>
          <div className="ms-auto limited-text mx-width-50" title={job.countryNames}>
            <div className="d-flex gap-1 infos align-items-center">
              <div className="flag-container limited-text">
                {jobCountryCodes.map((alpha2) => (
                  <ReactCountryFlag
                    key={alpha2}
                    countryCode={alpha2}
                    svg
                    style={{ width: "20px", marginLeft: "3px", height: "20px", borderRadius: 5 }}
                    //title={countries.getName(alpha2, "en")}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </li>
    );
  })}
</ul>
          </div>
        </div>

        {/* <div className="col-12 col-md-6 col-lg-3 mb-4">
                    <div className="card h-100 p-3">
                        <h6 className="mb-3">Calendar</h6>
                        <ul className="schedule-holder ps-0">
                            <p className="gray-bold fw-medium font-size-small mb-0">Today</p>
                            <li className="mb-3 position-relative today">
                                <p className="mb-1 fw-semibold mb-0">Front-End Developer</p>
                                <div className="d-flex gap-2">
                                    <p className="gray-bold font-size-small mb-0">Manager Round - Alen Austin</p>
                                    <p className="ms-auto gray-bold font-size-small mb-0">
                                        20/12/25
                                    </p>
                                </div>
                                <div className="d-flex gap-2">
                                    <p className="gray-bold font-size-small mb-0">Candidate - Nolen</p>

                                    <p className="ms-auto gray-bold font-size-small mb-0">
                                        12:00PM
                                    </p>
                                </div>

                            </li>
                            <li className="mb-3 position-relative today">
                                <p className="mb-1 fw-semibold mb-0">Front-End Developer</p>
                                <div className="d-flex gap-2">
                                    <p className="gray-bold font-size-small mb-0">Manager Round - Alen Austin</p>
                                    <p className="ms-auto gray-bold font-size-small mb-0">
                                        20/12/25
                                    </p>
                                </div>
                                <div className="d-flex gap-2">
                                    <p className="gray-bold font-size-small mb-0">Candidate - Nolen</p>

                                    <p className="ms-auto gray-bold font-size-small mb-0">
                                        12:00PM
                                    </p>
                                </div>

                            </li>
                            <p className="gray-bold fw-medium font-size-small mb-0">Upcoming</p>


                            <li className="mb-3 position-relative">
                                <p className="mb-1 fw-semibold mb-0">Front-End Developer</p>
                                <div className="d-flex gap-2">
                                    <p className="gray-bold font-size-small mb-0">Manager Round - Alen Austin</p>
                                    <p className="ms-auto gray-bold font-size-small mb-0">
                                        20/12/25
                                    </p>
                                </div>
                                <div className="d-flex gap-2">
                                    <p className="gray-bold font-size-small mb-0">Candidate - Nolen</p>

                                    <p className="ms-auto gray-bold font-size-small mb-0">
                                        12:00PM
                                    </p>
                                </div>

                            </li>
                            <li className="mb-3 position-relative">
                                <p className="mb-1 fw-semibold mb-0">Front-End Developer</p>
                                <div className="d-flex gap-2">
                                    <p className="gray-bold font-size-small mb-0">Manager Round - Alen Austin</p>
                                    <p className="ms-auto gray-bold font-size-small mb-0">
                                        20/12/25
                                    </p>
                                </div>
                                <div className="d-flex gap-2">
                                    <p className="gray-bold font-size-small mb-0">Candidate - Nolen</p>

                                    <p className="ms-auto gray-bold font-size-small mb-0">
                                        12:00PM
                                    </p>
                                </div>

                            </li>
                        </ul>
                    </div>
                </div> */}

        <div className="col-12 col-md-6 col-lg-3 mb-4">
          <div className="card h-100 p-3">
            <h6 className="mb-3">Upcoming Events</h6>
            <ul className="schedule-holder ps-0">

              {todayEvents.length > 0 && (
                <>
                  <p className="gray-bold fw-medium font-size-small mb-0">Today</p>
                  {todayEvents.map((event, idx) => (
                    <li key={`today-${idx}`} className="mb-3 position-relative today">
                      <p className="mb-1 fw-semibold">{event.jobTitle}</p>
                      <div className="d-flex gap-2">
                        <p className="gray-bold font-size-small mb-0">{event.round} - {event.actionTakenBy}</p>
                        <p className="ms-auto gray-bold font-size-small mb-0">
                          {new Date(event.activityDate).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="d-flex gap-2">
                        <p className="gray-bold font-size-small mb-0">Candidate - {event.candidateName}</p>
                        <p className="ms-auto gray-bold font-size-small mb-0">
                          {new Date(event.activityDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </li>
                  ))}
                </>
              )}

              {upcomingEvents.length > 0 && (
                <>
                  <p className="gray-bold fw-medium font-size-small mb-0">Upcoming</p>
                  {upcomingEvents.map((event, idx) => (
                    <li key={`upcoming-${idx}`} className="mb-3 position-relative">
                      <p className="mb-1 fw-semibold">{event.jobTitle}</p>
                      <div className="d-flex gap-2">
                        <p className="gray-bold font-size-small mb-0">{event.round} - {event.actionTakenBy}</p>
                        <p className="ms-auto gray-bold font-size-small mb-0">
                          {new Date(event.activityDate).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="d-flex gap-2">
                        <p className="gray-bold font-size-small mb-0">Candidate - {event.candidateName}</p>
                        <p className="ms-auto gray-bold font-size-small mb-0">
                          {new Date(event.activityDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </li>
                  ))}
                </>
              )}

            </ul>
          </div>
        </div>


      </div>
      <div className="row">




        <div className="col-12 col-md-6 col-lg-5 mb-4">
          <div className="card h-100 p-3">
            <h6 className="mb-3">Vacancy by Clients</h6>
            <div className="chart-holder">
              <Chart
                options={vacancyChartData?.options}
                series={vacancyChartData?.series}
                type="bar"
                height={300}
              />
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 col-lg-4 mb-4">
          <div className="card h-100 p-3">
            <h6 className="mb-3">Analytics</h6>
            <div className="chart-holder">
              <Chart
                options={analyticChartData?.options}
                series={analyticChartData?.series}
                type="donut"
                height={300}
              />
            </div>
          </div>
        </div>


        {/* <div className="col-12 col-md-6 col-lg-3 mb-4">
                    <div className=" h-100 ">
                        <h6 className="mb-3">Activity</h6>
                        <div className="activity-wrapper activity-log ">


                            <div className="activity-holder d-flex gap-3 position-relative">
                                <div className="dateInfo">
                                    <span>  <GoClock /> </span>
                                    20/12/24
                                </div>
                                <span className="icon">
                                </span>
                                <div className="activity-info">
                                    <p className="mb-0">4 New Application for Front-End
                                        Developer Post</p>
                                </div>
                            </div>

                            <div className="activity-holder d-flex gap-3 position-relative">
                                <div className="dateInfo">
                                    <span>  <GoClock /> </span>
                                    20/12/24

                                </div>
                                <span className="icon">
                                </span>
                                <div className="activity-info">
                                    <p className="mb-0">4 New Application for Front-End
                                        Developer Post</p>
                                </div>
                            </div>



                            <div className="activity-holder d-flex gap-3 position-relative">
                                <div className="dateInfo">
                                    <span>  <GoClock /> </span>
                                    20/12/24

                                </div>
                                <span className="icon">
                                </span>
                                <div className="activity-info">
                                    <p className="mb-0">4 New Application for Front-End
                                        Developer Post</p>
                                </div>
                            </div>


                            <div className="activity-holder d-flex gap-3 position-relative">
                                <div className="dateInfo">
                                    <span>  <GoClock /> </span>
                                    20/12/24

                                </div>
                                <span className="icon">
                                </span>
                                <div className="activity-info">
                                    <p className="mb-0">4 New Application for Front-End
                                        Developer Post</p>
                                </div>
                            </div>


                        </div>
                    </div>
                </div> */}


        <div className="col-12 col-md-6 col-lg-3 mb-4">
          <div className="h-100">
            <h6 className="mb-3">Activity</h6>
            <div className="activity-wrapper activity-log">
              {jobActivityLog.length > 0 ? (
                jobActivityLog.map((activity, idx) => (
                  <div className="activity-holder d-flex gap-3 position-relative" key={idx}>
                    <div className="dateInfo">
                      <span><GoClock /></span>
                      {new Date(activity.date).toLocaleDateString()}
                    </div>
                    <span className="icon"></span>
                    <div className="activity-info">
                      <p className="mb-0">{activity.message}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div>No activity found</div>
              )}
            </div>
          </div>
        </div>




      </div>

    </section>
  )
}
